<script setup lang="ts">
import { scroll, SECTION_SCROLL_OFFSET } from '@/shared/utils/scrollBehavior';
import type { NavigationLink } from '@/pages/lawyer/model/types/useLawyerDetail';

withDefaults(defineProps<{ items?: NavigationLink[] }>(), {
  items: () => [],
});

const isMobile: boolean = useIsMobile();
const activeTab: Ref<string> = ref('');

const changeTab = (tab: string): void => {
  activeTab.value = tab;
  scroll(document.querySelector(tab), SECTION_SCROLL_OFFSET);
};
</script>

<template>
  <nav :class="['navigation-links', { _mobile: isMobile }]">
    <ul class="navigation-links__list">
      <li v-for="{ id, text, to } in items" :key="id" class="navigation-links__item">
        <button
          :class="['navigation-links__button', { _active: activeTab === to }]"
          :data-scroll="to"
          type="button"
          :aria-label="text"
          @click.prevent="changeTab(to)"
        >
          {{ text }}
        </button>
      </li>
    </ul>
  </nav>
</template>

<style lang="less">
.navigation-links {
  margin: 0 -2px;

  &__list {
    display: flex;
    justify-content: space-between;
  }

  &__item {
    margin: 0 1px;
    padding: 15px;
    background: white;
    border: 1px solid @color-gallery;
    border-radius: @main-size;
    flex: 1;
    text-align: center;
  }

  &__button {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 600;
    color: @color-dove-gray;
  }

  &__button._active {
    font-weight: 600;
    color: @color-elephant;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: calc(100% + 5px);
      left: 0;
      width: 100%;
      background-color: @color-persimmon;
      height: 2px;
      border-radius: 1px;
    }
  }

  &._mobile {
    margin: 0;
  }

  &._mobile &__list {
    margin: 0;
  }

  &._mobile &__item {
    border-radius: 0;
    margin: 0;
    padding: @size-x3 @size-x2 @size-x3 0;

    &:first-child {
      border-radius: @main-size 0 0 @main-size;
      padding-left: @size-x2;
    }

    &:last-child {
      border-radius: 0 @main-size @main-size 0;
      padding-right: @size-x2;
    }

    &:not(:first-child) {
      border-left: 0;
      padding-left: 0;
    }

    &:not(:last-child) {
      border-right: 0;
    }
  }

  &._mobile &__button {
    font-size: 14px;
    line-height: 19px;

    &._active::after {
      top: calc(100% + 3px);
    }
  }
}
</style>
